import React                        from 'react'
import TextField                    from '@material-ui/core/TextField'
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import StyleSheet from 'styles'
import Switch from '@material-ui/core/Switch'
import Tab from '@material-ui/core/Tab';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import PageviewIcon from '@material-ui/icons/Pageview'
import { Select } from 'components/Select'
import AutoSuggest from 'components/AutoSuggest'
import { TimezonePicker } from 'components/TimezonePicker'
import DateTimePicker from 'components/DateTimePicker'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { humanize } from 'utils'
import NumberField from 'components/numbers/NumberField'
import {LinkButton} from 'components/LinkButton'

import Checkbox from 'components/styled/Checkbox'
import {eventKeyMatches, KEYS} from 'utils'

const FormComponentsMixin = superclass => class extends superclass{

  attributeChanged = attribute => event => {
    this.setAttribute(attribute, event.target.value)
  }

  setAttribute(attribute, value, callback) {
    const attributeChain = attribute.split('.')
    const attributes = {...this.state.attributes}
    const valueObject = attributeChain.slice(0, -1).reduce((result, att) => (result[att] = {...result[att]}), attributes)
    valueObject[attributeChain[attributeChain.length-1]] = value

    this.setState({attributes}, callback)
  }

  attribute = (name, defaultValue='') => {
    return this.deepGet(name, this.state.attributes, defaultValue)
  }

  deepGet(name, object, defaultValue='') {
    return name.split('.').reduce((memo, part) => memo ? memo[part] : null, object) || defaultValue
  }

  hasError = errorName =>
    this.errorObject && this.errorObject[errorName]

  getError = errorName =>
    (this.hasError(errorName) || []).join(' ')

  onEnterPressed = () => this.save()

  /**
   * Renders a @material-ui/core/Card
   */
  Card = props =>
    <div className={styles.flex}>
      <Card className={styles.card} {...props}/>
    </div>
  /**
   * Generate an onKeyPress handler
   */
  onKeyPressed = (key, callback) => (e) => {
    eventKeyMatches(e, key) && callback()
  }
  /**
   * Renders a @material-ui/core/CardContent
   * Calls `save` on the form if the ENTER_KEY was pressed
   */
  CardContent = ({children, ...props}) =>
    <CardContent className={styles.content} onKeyUp={this.onKeyPressed(KEYS.enter, this.onEnterPressed)} {...props}>{children}</CardContent>

  /**
   * Renders a @material-ui/core/CardActions
   * Calls `save` on the form if the ENTER_KEY was pressed
   */
  CardActions = ({children, ...props}) =>
    <CardActions onKeyUp={this.onKeyPressed(KEYS.enter, this.save)} {...props}>{children}</CardActions>

  Inline = ({children, ...props}) =>
    <div style={{display: 'inline-block', paddingRight: '20px'}}>
      {React.Children.map(children, (child) => {
        return (
          <div className={styles.inline}>
            {child}
          </div>
        )
      })}
    </div>

  /**
   * Renders a @material-ui/core/Typography
   */
  Typography = props =>
    <Typography {...props}/>

  /**
   * Renders a @material-ui/core/Button
   */
  Button = props =>
    <Button {...props}/>


  attributeSpecificProperties = (attribute) => {
    return attribute ? {
      onChange:  this.attributeChanged(attribute),
      value:     this.attribute(attribute),
      error:     this.hasError(attribute),
      helperText:this.getError(attribute),
      label:     humanize(attribute),
    } : {}
  }
  /**
   * Renders a DateField.
   * This uses the legacy @material-ui/core DateField.
   * Should be replaced with the new version once released
   */
  DateField = ({ attribute, focusFirst, options, ...rest }) => {
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense",
      showDate: true,
      openToYearSelection: true,
    }
    return <DateTimePicker options={options} name={attribute} {...defaults} {...rest}/>
  }

  /**
   * Renders a DateTimeField
   */
  DateTimeField = ({ attribute, focusFirst, options, ...rest }) => {
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense",
      openToYearSelection: true
    }
    return <DateTimePicker options={options} name={attribute} {...defaults} {...rest}/>
  }

  /**
   * Renders a TimezoneField
   */
  TimezoneField = ({ attribute, focusFirst, options, ...rest }) => {
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense"
    }
    return <TimezonePicker options={options} name={attribute} {...defaults} {...rest}/>
  }

  /**
   * Renders a TelephoneField
   */
  TelephoneField = ({ attribute, focusFirst, ...rest }) => {
    const props = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense",
      ...rest
    }

    const changeHandler = props.onChange
    props.onChange = (event) => {
      event.target.value = String(event.target.value).replace(/[^0-9\-+()]/g,'')
      changeHandler(event)
    }
    return <TextField name={attribute}  type='tel' {...props}/>
  }

  /**
   * Renders a SelectField
   */
  SelectField = ({ attribute, focusFirst, options, ...rest }) => {
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense"
    }
    return <Select options={options} name={attribute} {...defaults} {...rest}/>
  }

  /**
   * Renders a NumberField
   */
  NumberField = ({ attribute, focusFirst, ...rest }) => {
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense"
    }
    return <TextField name={attribute} type='number' {...defaults} {...rest}/>
  }

  /**
   * Renders a LargeNumberField
   */
  LargeNumberField = ({ attribute, focusFirst, ...rest }) => {
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense"
    }
    return <NumberField name={attribute} {...defaults} {...rest}/>
  }

  /**
   * Renders a FileUploadField
   */
  FileUploadField = ({buttonProps=false, attribute, focusFirst, maxSize, src, acceptedTypes, alt, ...rest }) => {
    maxSize = maxSize || parseFloat(process.env.REACT_APP_MAX_UPLOAD_SIZE)
    const attributeId = this.getAttributeContext ? `${this.getAttributeContext()}-${attribute}` : attribute
    const isButton = !!buttonProps
    const props = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense",
      ...rest
    }
    const currentValue = this.attribute(attribute)
    const onChange     = ({target: { files: [file] }}) => {
      if(!file){
        return
      }
      if(file.size < maxSize) {
        const reader = new FileReader()
        reader.onload = (event) => {
          file.asDataURL = event.target.result
          this.setAttribute(attribute, file)
        }
        reader.readAsDataURL(file)
      }else{
        alert(`File exceeds max upload size of ${(maxSize / 1024 / 1024).toFixed(2)}MB`)
      }
    }
    const launchFileSelect = () => {
      // dots in the id CSS selector must be escaped
      // eg. "#companyInfo\\.certificationOfIncorporation"
      const sanitizedId = attributeId.replace(/\./g, '\\.')
      document.querySelector(`#${sanitizedId}`).click()
    }

    const showThumbnail = event => {
      if(!currentValue){
        window.open(src, {target: '_blank'})
      }
      event.stopPropagation()
    }

    let fileName = (currentValue && currentValue.name)
    if(!fileName) {
      fileName = this.attribute(`${attribute}FileName`)
    }

    // find a way to grab this from theme.MuiFormLabel.root
    let inputProps = { style: {
        lineHeight: '11px',
        fontSize: '13px',
        fontWeight: 'normal'
      }
    }
    if(acceptedTypes) {
      inputProps = {...inputProps, accept: acceptedTypes}
    }

    const imgSrc = (currentValue && currentValue.asDataURL) || src
    return (
      <div className={styles.datepickerWrapper} onClick={launchFileSelect}>
        {
          !isButton &&
          <TextField id={`file-upload-label-${attributeId}`} {...props} value={fileName} onFocus={launchFileSelect} onKeyDown={e => e.preventDefault()} style={{pointerEvents: 'none'}}/>
        }
        <TextField id={attributeId} inputProps={inputProps} className={styles.inputfile} type='file' onChange={onChange}/>
        { imgSrc &&
          <LinkButton className={styles.thumbnailLink} onClick={showThumbnail}>
            <img alt='' className={styles.thumbnail} src={imgSrc}/>
          </LinkButton>
        }
        {
          isButton &&
          <Button {...buttonProps}>Browse</Button>
        }
      </div>
    )
  }

  /**
   * Renders a SwitchField
   */
  SwitchField = ({attribute, focusFirst, mapCheckedToValue, mapValueToChecked, ...rest }) => {
    const defaults = this.attributeSpecificProperties(attribute)
    const props = {...defaults, ...rest }

    if(!mapCheckedToValue) {
      mapCheckedToValue = (checked) => {
        return checked
      }
    }
    if(!mapValueToChecked) {
      mapValueToChecked = (value) => {
        return !!value
      }
    }

    return (
        <FormControl onClick={rest.onClick} error={props.error} fullWidth={true}>
          <FormControlLabel
            control={
              <Switch
                id={`${attribute}-switch`}
                checked={ mapValueToChecked(props.value) }
                disabled={rest.disabled}
                onChange={(event, checked) =>  this.attributeChanged(attribute)({ target: { value: mapCheckedToValue(checked) }})}
              />
            }
            label={props.label}
          />
          {props.helperText && <FormHelperText>{props.helperText}</FormHelperText>}
        </FormControl>
    )
  }

  /**
   * Renders a CheckboxField
   */
  CheckboxField = ({attribute, focusFirst, mapCheckedToValue, mapValueToChecked, ...rest }) => {
    const defaults = this.attributeSpecificProperties(attribute)
    const props = {...defaults, ...rest }

    if(!mapCheckedToValue) {
      mapCheckedToValue = (checked) => {
        return checked
      }
    }
    if(!mapValueToChecked) {
      mapValueToChecked = (value) => {
        return !!value
      }
    }

    return (
        <FormControl onClick={rest.onClick} error={props.error} fullWidth={true}>
          <FormControlLabel
            control={
              <Checkbox
                id={`${attribute}-switch`}
                checked={ mapValueToChecked(props.value) }
                disabled={rest.disabled}
                onChange={({target, target: {checked}}) =>
                  this.attributeChanged(attribute)({ target: { value: mapCheckedToValue(checked) }})
                }
              />
            }
            label={props.label}
          />
          {props.helperText && <FormHelperText>{props.helperText}</FormHelperText>}
        </FormControl>
    )
  }

  /**
   * Renders a @material-ui/core/TextField that is bound to a single attribute on
   * the `formObject` prop.
   * Also shows an error state based on an attribute of the same name on the `errorObject` prop
   */
  TextField = ({ attribute, focusFirst, ...rest }) => {
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense"
    }
    const { multiline, onKeyUp } = rest
    if(multiline) {
      const onKeyUpWrapper = (event) => {
        if(eventKeyMatches(event, KEYS.enter)) {
          event.stopPropagation()
        }

        if(onKeyUp)  {
          onKeyUp(event)
        }
      }
      rest = { ...rest, onKeyUp: onKeyUpWrapper }
    }

    return <TextField name={attribute}  {...defaults} {...rest}/>
  }

  MarkDownField = ({onRequestOpenPreview, onRequestOpenHelp, showPreviewButton = true, showHelpButton, ...attributes}) => {
    const { TextField } = this
    const requestPreview = onRequestOpenPreview ? onRequestOpenPreview : () => {}
    const requestHelp = onRequestOpenHelp ? onRequestOpenHelp : () => {}

    return (
      <div>
        <TextField multiline rows={5} rowsMax={15} inputProps={{ className: styles.markDownField}} {...attributes} InputLabelProps={{shrink: true}}/>
        {showPreviewButton &&
          <Button onClick={requestPreview} color="primary" aria-label="Preview">
            Preview
            <PageviewIcon className={styles.rightIcon}/>
          </Button>
        }
        { showHelpButton &&
          <Button onClick={requestHelp} color="primary" aria-label="Preview">
            Help
            <HelpOutlineIcon className={styles.rightIcon}/>
          </Button>
        }
      </div>
    )
  }


  /**
   * Renders a AutoSuggest that is bound to a single attribute on
   * the `formObject` prop.
   * Also shows an error state based on an attribute of the same name on the `errorObject` prop
   */
  AutoSuggest = ({ attribute, focusFirst, labelProviderProp, onSuggestionsFetchRequested, itemAttribute = 'name', ...rest }) => {
    const labelProvider = labelProviderProp || (({[itemAttribute]: label}) => label)
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      onChange: ({target: {value}}) => {
        this.setAttribute(attribute, value, () => this.setAttribute(attribute + 'Id', value ? value.id : ''))
      },
      labelProvider: labelProvider,
      onSuggestionsFetchRequested: (text, callback, error) => onSuggestionsFetchRequested(text).then(callback).catch(error),
      value: (attribute && this.attribute(attribute)) ? labelProvider(this.attribute(attribute)) : '',
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense"
    }
    const { onKeyUp } = rest
    const onKeyUpWrapper = (event) => {
      if(eventKeyMatches(event, KEYS.enter)) {
        event.stopPropagation()
      }

      if(onKeyUp)  {
        onKeyUp(event)
      }
    }
    rest = { ...rest, onKeyUp: onKeyUpWrapper }

    return <AutoSuggest name={attribute}  {...defaults} {...rest}/>
  }

  /**
   * Renders a MultiAutoSuggest that is bound to a single attribute on
   * the `formObject` prop.
   * Also shows an error state based on an attribute of the same name on the `errorObject` prop
   */
  MultiAutoSuggest = ({ attribute, focusFirst, ...rest }) => {
    const defaults = {
      ...this.attributeSpecificProperties(attribute),
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense"
    }

    return <AutoSuggest name={attribute}  {...defaults} {...rest}/>
  }

  fieldsForObject = (objectAttribute) => {
    let objectComponents
    const objectComponentsClass = class extends FormComponentsMixin(Object) {
      attributeChanged = attribute => event => {
        objectComponents.setAttribute(attribute, event.target.value)
      }
      attribute = (name, defaultValue = '') => {
        return this.attribute(objectAttribute, {})[name] || defaultValue
      }
      setAttribute = (name, value, callback) => {
        this.setAttribute(objectAttribute, {...this.attribute(objectAttribute, {}), [name]: value}, callback)
      }
      hasError = (errorName) => {
        return this.hasError(`${objectAttribute}.${errorName}`)
      }
      getError = (errorName) => {
        return this.getError(`${objectAttribute}.${errorName}`)
      }
      getAttributeContext = () => {
        return objectAttribute
      }
    }
    objectComponents = new objectComponentsClass()
    return objectComponents
  }

  /**
   * Renders a @material-ui/core/Tab that turns red if the `error` property is set to true.
   */
  Tab = (props) => {
    let { error, className, ...rest } = props
    let classes = []

    if(error) {
      classes.push(styles.tabWithErrors)
    }

    if(className) {
      className = className.trim()
      if(className.length > 0) {
        classes.push(className)
      }
    }

    className = classes.join(',')
    return <Tab className={className} {...rest} />
  }

  /**
   * Renders a Select field with valid combinations for M of N attributes.
   */
  WalletMNField = ({ focusFirst, ...rest }) => {
    const defaults = {
      autoFocus: focusFirst && window.innerWidth > 500,
      fullWidth: true,
      margin:    "dense"
    }

    const helperText = () => {
      const s = new Set([this.getError('wallet.m'), this.getError('wallet.n')])
      return [...s].join(', ')
    }

    const value = () => {
      const n = this.attribute('wallet.n')
      const m = this.attribute('wallet.m')
      return `${m}_${n}`
    }
    const options = [
      {label: '2 of 3', value: '2_3' },
      {label: '3 of 5', value: '3_5' }
    ]
    const onChange = ({target: { value }}) => {
      const match = value.match(/(.*)_(.*)/)
      const [ , m, n] = match
      this.setAttribute('wallet.m', m, () => {
        this.setAttribute('wallet.n', n)
      })
    }

    return <Select options={options}
                   value={ value() } onChange={ onChange }
                   error={ this.hasError('wallet.m') || this.hasError('wallet.n') }
                   helperText={ helperText() }
                   {...defaults} {...rest}/>
  }
}

const styles = StyleSheet({
  flex: {
    margin: 10,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    flex: 1,
    '@media(max-width: 500px)': {
      margin: 0
    }
  },
  card: {
    maxWidth: 900,
    flex: 1,
    width: '100%',
    boxSizing: 'border-box'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputfile: {
    display: 'none'
  },
  datepickerWrapper: {
    cursor: 'pointer'
  },
  fileUploadLabelWrap: {
    width: '100%',
    '& p':{
      width: '100%',
      fontSize: 15
    }
  },
  fileUploadLabel: {
    paddingBottom: 5,
    width: '100%',
    borderBottom: '1px solid silver',
    margin: '20px 0',
    color: '#c7c7c7',
  },
  thumbnailLink: {
    textAlign: 'center',
    display: 'block'
  },
  thumbnail: {
    maxWidth: '100%',
    maxHeight: 330,
    margin: '0 auto',
    width: 'auto'
  },
  inline: {
    display: 'inline-block',
    paddingRight: '20px'
  },
  tabWithErrors: {
    fontWeight: 'bold',
    color: '#EA5142'
  },
  textFieldWithButtonWrapper: {
    display: 'flex',
    flexDirection: 'row'
  },
  markDownField: {
    fontFamily: 'Consolas,"Liberation Mono",Courier,monospace',
    fontSize: '14px',
    fontWeight: '400',
    fontStyle: 'normal',
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.75)',
  },
  rightIcon: {
    paddingLeft: '8px'
  }
})
export default FormComponentsMixin